/* utils/columnConfig.js
   ------------------------------------------------------------------
   Returns column definitions for each HR-request tab.
   Each entry: { label: string, accessor: (req) => any }
-------------------------------------------------------------------*/

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString();
}

function formatRequestType(type) {
  return String(type || 'N/A')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getFinalStatus(r) {
  if (!r) return 'Pending';

  if (String(r?.request_type || '').toLowerCase() === 'pre_approval') {
    const pre = String(r?.pre_approval_status || '').toLowerCase();
    if (pre === 'revision_requested') return 'Revision';
    if (pre === 'fully_approved') return 'Approved';
    if (pre === 'rejected') return 'Rejected';
    return 'Pending';
  }

  const chain = [
    r.status,
    r.hr_status,
    r.finance_coordinator_status,
    r.finance_status,
    r.ceo_status,
  ].filter((v) => v != null && String(v).trim() !== '');

  if (!chain.length) return 'Pending';

  const upper = chain.map((v) => String(v).toUpperCase());
  if (upper.includes('REJECTED')) return 'Rejected';
  if (upper.every((v) => v === 'APPROVED')) return 'Approved';
  if (upper.includes('PENDING')) return 'Pending';

  return String(chain[chain.length - 1] || 'Pending');
}

/* status helpers */
const managerStatus  = (r) => r.status          || 'Pending';
const hrStatus       = (r) => r.hr_status       || 'Pending';
const financeStatus  = (r) => r.finance_status  || 'Pending';
const ceo_status  = (r) => r.ceo_status  || 'Pending';
const finance_coordinator  = (r) => r.finance_coordinator_status  || 'Pending';

export function getColumnsForTab(tabKey) {
  switch (tabKey) {
    case 'all':
      return [
        { label: 'Request Date', accessor: (r) => formatDate(r.request_date || r.created_at) },
        { label: 'Type', accessor: (r) => formatRequestType(r.request_type) },
        { label: 'Details', accessor: (r) => r.notes || r.description || 'N/A' },
        { label: 'Final Status', accessor: (r) => getFinalStatus(r) },
      ];

    /* ───────────────────────────────────────────────────────────
       1) LEAVE (to_leave)
    ─────────────────────────────────────────────────────────── */
    case 'to_leave':
      return [
        { label:'Request Date', accessor:(r)=>formatDate(r.request_date) },
        { label:'From Date',    accessor:(r)=>formatDate(r.from_date)   },
        { label:'To Date',      accessor:(r)=>formatDate(r.to_date)     },
        { label:'Leave Type',   accessor:(r)=>r.leave_type || 'N/A'     },
        { label:'Manager Status', accessor:managerStatus },
        { label:'HR Status',      accessor:hrStatus      },
      ];

    /* ───────────────────────────────────────────────────────────
       2) LOANS
    ─────────────────────────────────────────────────────────── */
    case 'loans':
      return [
        { label:'Request Date', accessor:(r)=>formatDate(r.request_date) },
        { label:'Loan Date',    accessor:(r)=>formatDate(r.loan_date)    },
        { label:'Loan Period',  accessor:(r)=>r.loan_period ?? 'N/A'     },
        { label:'No. of Payments', accessor:(r)=>r.no_of_payments ?? 'N/A'},
        { label:'Amount',       accessor:(r)=>r.amount ? `${r.amount} SAR` : 'N/A' },
        { label:'HR Status',      accessor:hrStatus     },
        { label:'Finance Status', accessor:financeStatus},
        { label:'CEO Status', accessor:ceo_status},
      ];

    /* ───────────────────────────────────────────────────────────
       3) FINANCE CLAIM
    ─────────────────────────────────────────────────────────── */
    case 'finance_claim':
      return [
        { label:'Request Date', accessor:(r)=>formatDate(r.request_date) },
        { label:'Description',  accessor:(r)=>r.description || 'N/A'     },
        { label:'Amount',       accessor:(r)=>r.amount ? `${r.amount} SAR` : 'N/A' },
        { label:'HR Status',      accessor:hrStatus     },
        { label:'Finance Coordinator Status',accessor:finance_coordinator},
        { label:'Finance Status', accessor:financeStatus},
      ];

    /* ───────────────────────────────────────────────────────────
       4) MISC
    ─────────────────────────────────────────────────────────── */
    case 'misc':
      return [
        { label:'Request Date', accessor:(r)=>formatDate(r.request_date) },
        { label:'Requested Item', accessor:(r)=>r.requested_item || 'N/A' },
        { label:'Temporary?',     accessor:(r)=>r.is_temporary ? 'Yes' : 'No' },
        { label:'Manager Status', accessor:managerStatus },
        { label:'HR Status',      accessor:hrStatus      },
      ];

    /* ───────────────────────────────────────────────────────────
       5) BUSINESS TRIP
    ─────────────────────────────────────────────────────────── */
    case 'business_trip':
      return [
        { label:'Request Date', accessor:(r)=>formatDate(r.request_date) },
        { label:'Trip Type',    accessor:(r)=>formatRequestType(r.business_trip_type) },
        { label:'Region',       accessor:(r)=>r.business_trip_region || 'N/A' },
        { label:'Destination',  accessor:(r)=>formatRequestType(r.business_trip_destination) },
        { label:'Description',  accessor:(r)=>r.description || 'N/A' },
        { label:'Manager Status', accessor:managerStatus },
        { label:'HR Status',      accessor:hrStatus      },
      ];

    /* ───────────────────────────────────────────────────────────
       6) BANK
    ─────────────────────────────────────────────────────────── */
    case 'missing_punch':
      return [
        { label:'Request Date', accessor:(r)=>formatDate(r.request_date || r.missing_punch_date) },
        { label:'Punch Date',   accessor:(r)=>formatDate(r.missing_punch_date) },
        { label:'Punch Type',   accessor:(r)=>formatRequestType(r.missing_punch_type) },
        { label:'Punch Time',   accessor:(r)=>r.missing_punch_time || 'N/A' },
        { label:'Project',      accessor:(r)=>r.missing_punch_project?.name || r.missingPunchProject?.name || r.missing_punch_project_id || 'N/A' },
        { label:'HR Status',    accessor:hrStatus },
      ];

    case 'bank':
      return [
        { label:'Request Date', accessor:(r)=>formatDate(r.request_date) },
        { label:'Bank Name',    accessor:(r)=>r.bank_name   || 'N/A' },
        { label:'Branch',       accessor:(r)=>r.bank_branch || 'N/A' },
        { label:'Account #',    accessor:(r)=>r.new_account_number || 'N/A' },
        { label:'HR Status',    accessor:hrStatus },
      ];

    /* ───────────────────────────────────────────────────────────
       7) PERSONAL-DATA CHANGE  ← NEW
    ─────────────────────────────────────────────────────────── */
    case 'personal_data_change':
      return [
        { label:'Request Date', accessor:(r)=>formatDate(r.request_date) },
        { label:'Field',        accessor:(r)=>r.personal_data_field  || 'N/A' },
        { label:'New Value',    accessor:(r)=>r.personal_data_value || 'N/A' },
        { label:'Manager Status', accessor:managerStatus },
        { label:'HR Status',      accessor:hrStatus      },
      ];

    case 'pre_approval':
      return [
        { label:'Request Date', accessor:(r)=>formatDate(r.request_date || r.created_at) },
        { label:'Reference', accessor:(r)=>r.pre_approval_reference || 'N/A' },
        { label:'Candidate', accessor:(r)=>r.candidate_name || 'N/A' },
        { label:'Position', accessor:(r)=>r.position_offered || 'N/A' },
        { label:'Current Step', accessor:(r)=>String(r.pre_approval_current_step || 'pending').replace(/_/g, ' ') },
        { label:'Final Status', accessor:(r)=>getFinalStatus(r) },
      ];

    /* ───────────────────────────────────────────────────────────
       DEFAULT / unknown
    ─────────────────────────────────────────────────────────── */
    default:
      return [
        { label:'Request Date', accessor:(r)=>formatDate(r.request_date) },
        { label:'Request Type', accessor:(r)=>r.request_type || 'N/A'    },
        { label:'Manager Status', accessor:managerStatus },
        { label:'HR Status',      accessor:hrStatus      },
      ];
  }
}
